<script setup lang="ts">
import type { LoginStation } from '~/utils/login-stations'

const props = defineProps<{
  stations: LoginStation[]
  modelValue: string
}>()

const emit = defineEmits<{
  'update:modelValue': [id: string]
  'hover': [value: boolean]
  'lock': []
}>()

const cover = ref<HTMLElement | null>(null)
const image = ref<HTMLImageElement | null>(null)
const loaded = ref(false)
const reduceMotion = ref(false)
const shift = ref({ x: 0, y: 0 })
const stage = ref({ width: 0, height: 0, x: 0, y: 0 })

const stageStyle = computed(() => {
  if (!stage.value.width) return undefined
  return {
    width: `${stage.value.width}px`,
    height: `${stage.value.height}px`,
    transform: `translate3d(${stage.value.x + shift.value.x}px, ${stage.value.y + shift.value.y}px, 0)`
  }
})

function layoutStage() {
  const el = cover.value
  if (!el) return
  const { width, height } = el.getBoundingClientRect()
  if (!width || !height) return

  const pad = reduceMotion.value ? 0 : 28
  const scale = Math.max(width / 1024, height / 682) * (pad ? 1.1 : 1)
  const stageWidth = 1024 * scale
  const stageHeight = 682 * scale
  const desktop = window.matchMedia('(min-width: 1024px)').matches
  const focusX = desktop ? 0.34 : 0.48
  const focusY = desktop ? 0.66 : 0.56
  const x = Math.min(-pad, Math.max(width - stageWidth + pad, width * 0.4 - stageWidth * focusX))
  const y = Math.min(-pad, Math.max(height - stageHeight + pad, height * 0.62 - stageHeight * focusY))
  stage.value = { width: stageWidth, height: stageHeight, x, y }
}

function markLoaded() {
  loaded.value = true
}

function select(id: string, lock: boolean) {
  emit('update:modelValue', id)
  if (lock) emit('lock')
}

function onPointerMove(event: PointerEvent) {
  if (reduceMotion.value || !cover.value || event.pointerType === 'touch') return
  const rect = cover.value.getBoundingClientRect()
  const px = (event.clientX - rect.left) / rect.width - 0.5
  const py = (event.clientY - rect.top) / rect.height - 0.5
  shift.value = { x: px * -14, y: py * -10 }
}

function onPointerLeave() {
  shift.value = { x: 0, y: 0 }
  emit('hover', false)
}

let observer: ResizeObserver | undefined

onMounted(() => {
  reduceMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (image.value?.complete) loaded.value = true
  layoutStage()
  if (cover.value) {
    observer = new ResizeObserver(() => layoutStage())
    observer.observe(cover.value)
  }
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<template>
  <section
    ref="cover"
    class="relative isolate min-h-[14rem] h-[32dvh] overflow-hidden bg-[#1c1917] lg:h-full"
    aria-label="Taller de bordado de Industrias Monzón"
    @pointerenter="emit('hover', true)"
    @pointermove="onPointerMove"
    @pointerleave="onPointerLeave"
  >
    <div class="absolute top-0 left-0 overflow-hidden" :style="stageStyle">
      <div
        class="absolute inset-0"
        :class="{ 'login-stage-drift': !reduceMotion && stage.width }"
      >
        <img
          ref="image"
          src="/images/portada-taller.jpg"
          alt=""
          class="size-full object-cover transition-opacity duration-700"
          :class="loaded ? 'opacity-100' : 'opacity-0'"
          @load="markLoaded"
        >
        <button
          v-for="station in props.stations"
          :key="station.id"
          type="button"
          class="group absolute z-10 hidden size-11 -translate-x-1/2 -translate-y-1/2 lg:grid lg:place-items-center"
          :class="props.modelValue === station.id ? 'z-20' : ''"
          :style="{ top: station.top, left: station.left }"
          tabindex="-1"
          aria-hidden="true"
          @pointerenter="select(station.id, false)"
          @focus="select(station.id, true)"
          @click="select(station.id, true)"
        >
          <span
            class="absolute inset-1 rounded-full border bg-white/85 shadow-[0_1px_3px_rgba(28,25,23,0.28)] transition-transform duration-300"
            :class="props.modelValue === station.id
              ? 'scale-110 border-[#9a7340]'
              : 'border-white group-hover:scale-105'"
          />
          <span
            v-if="props.modelValue === station.id && !reduceMotion"
            class="login-ring absolute inset-0 rounded-full border border-white/80"
            aria-hidden="true"
          />
          <span
            class="relative size-2 rounded-full"
            :class="props.modelValue === station.id ? 'bg-[#9a7340]' : 'bg-[#1c1917]'"
          />
          <span
            class="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-[#1c1917]/90 px-2.5 py-1 text-xs font-medium text-white transition-opacity duration-300"
            :class="[
              station.labelAlign === 'right' ? 'left-full ml-2.5' : 'right-full mr-2.5',
              props.modelValue === station.id ? 'opacity-100' : 'opacity-0'
            ]"
          >
            {{ station.index }} {{ station.title }}
          </span>
        </button>
      </div>
    </div>

    <div class="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#1c1917]/35 to-transparent lg:hidden" />
  </section>
</template>

<style scoped>
.login-stage-drift {
  animation: login-drift 36s cubic-bezier(0.45, 0, 0.55, 1) infinite alternate;
}

.login-ring {
  animation: login-ring 2.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

@keyframes login-drift {
  from {
    scale: 1;
  }
  to {
    scale: 1.03;
  }
}

@keyframes login-ring {
  0% {
    opacity: 0.7;
    transform: scale(0.85);
  }
  100% {
    opacity: 0;
    transform: scale(1.65);
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-stage-drift,
  .login-ring {
    animation: none;
  }
}
</style>
